import { Injectable } from '@angular/core';
import { WebservicesService } from '../webservices/webservices.service';
import { EventsService } from '../event/event.service';
import { ConfigService } from '../config/config.service';
import { AdvertisingId } from "@capacitor-community/advertising-id";
import { AppsflyerService } from '../marketing/appsflyer.service';
import { UserSessionService } from '../user-session/user-session.service';
import { Navigator } from "src/app/components/menu/navigator";
import { Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class TrackConsentService {

  authorizationStatus = {
    AUTHORIZED: 'Authorized',
    DENIED: 'Denied',
    NOT_DETERMINED: 'Not Determined',
    RESTRICTED: 'Restricted'
  };

  private consentMessage = {
    title: "",
    message: ""
  }

  constructor(
    private webservice: WebservicesService,
    private eventService: EventsService,
    private config: ConfigService,
    private appsFlyer: AppsflyerService,
    private userSession: UserSessionService,
    private navigator: Navigator,
    private platform: Platform) { }

  public checkTrackConsentShouldDisplay() {

   if(!this.platform.is('ios') || !this.platform.is('capacitor')) return false;
   
    const isTrackAlreadySet = this.appsFlyer.getStoredAppsflyerData();
    const trackStatus = this.appsFlyer.trackStatus;
  
    if (isTrackAlreadySet && trackStatus.status !== this.authorizationStatus.NOT_DETERMINED) {
      this.sendCustomerTrackingRequests();
      return false;
    }
  
    const shouldDisplay = trackStatus.status === this.authorizationStatus.NOT_DETERMINED &&
      this.checkTrackConsentConfigAllowed();
    return shouldDisplay;
  }

  public async checkAdvertisingStatus() {
    try {
      const trackStatus = await AdvertisingId.getAdvertisingStatus();
      if (this.authorizationStatus.NOT_DETERMINED === trackStatus.status) {
        this.setWebConfigTitles();
        this.consentMessagePopover();
      } else {
        this.eventService.publish('event:appInit:next');
        this.startAllowedTracking();
      }
    } catch (e) { console.log(e) }
  }

  private setWebConfigTitles() {
    const consentConfig = this.config.getConfig().FEATURE_TRACK_CONSENT_MESSAGE;

    this.consentMessage = consentConfig ? JSON.parse(consentConfig) : {
      title: "Enable Activity Tracking for Stardust Social Casino",
      message: "You will encounter a prompt to allow Stardust Social Casino to track your activity in this app.",
      message2: "This data helps Boyd Gaming, the company who built this app, know if its marketing worked."
    };
  }

  private async consentMessagePopover() {
    try {
      let closeModel = async () => {
        await AdvertisingId.requestTracking();
        this.eventService.publish('event:appInit:next');
        await this.waitForTrackingStatus();
        this.startAllowedTracking();
      }
      let data: any = {
        content: this.consentMessage,
        expectResult: true,
        onCloseFunc: closeModel
      };
      this.openPopover(data);

    } catch (e) { console.error(e) }
  }
  protected openPopover(data) {
    const POPUP_DELAY = 5000;
    setTimeout(() => {
      if (this.checkPopoverActive()) {
        this.navigator.openPopover("PopoverTrackConsentComponent", data,
          "standard-popover track-popover", true, true, true, -1, 'cover', false, true);
      } else {
        // Recursively attempt to open the popover if the previous conditions weren't met
        this.openPopover(data);
      }
    }, POPUP_DELAY);
  }

  protected checkPopoverActive() {
    return this.navigator.activeModals.length === 0 &&
      this.navigator.activePopovers.length === 0 &&
      this.navigator.activePriorityPopovers.length === 0;
  }

  protected async startAllowedTracking() {
    try {
      await this.appsFlyer.loadAndStoredTrackingIDs();
      this.sendCustomerTrackingRequests();
    } catch (e) { console.error(e) }
  }

  protected async waitForTrackingStatus(): Promise<string> {
    const MAX_TRIES = 10;
    const INTERVAL_MS = 3000;

    return new Promise<string>((resolve, reject) => {
      let tries = 0;
      const timeInterval = setInterval(async () => {
        try {
          tries++;
          const trackStatus = await AdvertisingId.getAdvertisingStatus();

          if (this.authorizationStatus.NOT_DETERMINED !== trackStatus.status || tries >= MAX_TRIES) {
            clearInterval(timeInterval);
            resolve(trackStatus.status);
          }
        } catch (error) {
          clearInterval(timeInterval);
          resolve(null);
        }
      }, INTERVAL_MS);
    });
  }

  private sendCustomerTrackingRequests() {
    const trackingData = this.appsFlyer.getTrackingIDs();

    const data: any = {
      session_token: this.userSession.getSessionToken(),
      device_details: trackingData?.deviceDetails || undefined,
      external_source: trackingData?.externalSource || undefined
    };

    // Remove undefined properties from data
    Object.keys(data).forEach(key => data[key] === undefined && delete data[key]);

    return this.webservice.post('customer/save-customer-tracking-info', data);
  }

  public checkTrackConsentConfigAllowed() {
    return this.config.getConfig()?.FEATURE_ALLOW_TRACKING_CONSENT == 1 || false;
  }

  public async getTrackingStatus(): Promise<string> { // Return type changed to string for tracking status
    try {
      const trackStatus = await AdvertisingId.getAdvertisingStatus();
      
      if (this.authorizationStatus.AUTHORIZED == trackStatus.status) {
        return trackStatus.status;
      } else {
        const requestStatus = await AdvertisingId.requestTracking();
        if (this.authorizationStatus.AUTHORIZED == requestStatus.value) {
          return requestStatus.value;
        } else {
          let consentMessage = {
            title: "Enable Activity Tracking for Stardust Social Casino",
            message: "Please enable tracking for a better experience with Stardust Social Casino.",
            message2: "Go to: Settings > Privacy & Security > Tracking > Enable Stardust Social Casino"
          };
          let data: any = {
            content: consentMessage,
            expectResult: true,
          };
          await this.navigator.openPopover("PopoverTrackConsentComponent", data,
            "standard-popover track-popover", true, true, true, -1, 'cover', false, true);
  
          return requestStatus.value;
        }
      }
    } catch (error) {
      console.error("Error getting tracking status:", error);
      return 'UNKNOWN'; 
    }
  }
}
